import { Compact, DeepCompact } from "@/types/util"

export function integerArray(length: number): number[] {
  return Array.from(Array(length), (x, i) => i)
}

export function ensureArray<T>(anything: T | T[]): T[] {
  return Array.isArray(anything) ? anything : [anything]
}

export function isNumberArray(value: unknown): value is number[] {
  return Array.isArray(value) && value.every((item) => typeof item === "number")
}

export function keys<K extends string, V>(object: Record<K, V>) {
  return Object.keys(object) as K[]
}

export function freeze<T>(value: T): T {
  return Object.freeze(value)
}

const constantEmptyArray = [] as unknown[]
export function getConstantEmptyArray<T>(): T[] {
  return constantEmptyArray as T[]
}

const constantEmptyObject = {} as Record<string, unknown>
export function getConstantEmptyObject<T>(): T {
  return constantEmptyObject as T
}

const constantEmptySet = new Set<unknown>()
export function getConstantEmptySet<T>(): Set<T> {
  return constantEmptySet as Set<T>
}

export function compact<T extends Record<string, unknown> | Array<unknown>>(
  obj: T
): Compact<T> {
  if (Array.isArray(obj)) {
    // Filter out null and undefined elements from the array
    const result = obj.filter((value) => value !== null && value !== undefined)
    return result as Compact<T>
  }
  if (typeof obj === "object" && obj !== null) {
    const result: Record<string, unknown> = {}
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const value = obj[key]
        if (value !== null && value !== undefined) {
          result[key] = value
        }
      }
    }
    return result as Compact<T>
  }

  // If it's a primitive value, return it as is
  return obj as Compact<T>
}

export function deepCompact<T extends Record<string, unknown> | Array<unknown>>(
  obj: T,
  cache = new WeakSet()
): DeepCompact<T> {
  if (typeof obj !== "object" || obj === null) {
    return obj as DeepCompact<T>
  }

  if (cache.has(obj)) {
    throw new Error("circular reference")
  }

  cache.add(obj)

  if (Array.isArray(obj)) {
    const resultArray = []
    for (const item of obj) {
      if (item !== null && item !== undefined) {
        if (item instanceof RegExp) {
          resultArray.push(item)
        } else if (typeof item === "object") {
          resultArray.push(deepCompact(item, cache))
        } else {
          resultArray.push(item)
        }
      }
    }
    return resultArray as unknown as DeepCompact<T>
  }

  const result: Record<string, unknown> = {}

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key]
      if (value !== null && value !== undefined) {
        if (value instanceof RegExp) {
          result[key] = value
        } else if (Array.isArray(value)) {
          result[key] = deepCompact(value, cache)
        } else if (typeof value === "object") {
          result[key] = deepCompact(value as Record<string, unknown>, cache)
        } else {
          result[key] = value
        }
      }
    }
  }

  return result as DeepCompact<T>
}

export function compactBlank<T>(obj: T): T {
  if (Array.isArray(obj)) {
    return obj
      .map(compactBlank)
      .filter(
        (v) =>
          v !== null &&
          v !== undefined &&
          !(typeof v === "string" && v.trim() === "") &&
          !(Array.isArray(v) && v.length === 0) &&
          !(typeof v === "object" && true && Object.keys(v).length === 0)
      ) as T
  }
  if (typeof obj === "object" && obj !== null) {
    const result: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(obj)) {
      const cleaned = compactBlank(v)
      if (
        cleaned !== null &&
        cleaned !== undefined &&
        !(typeof cleaned === "string" && cleaned.trim() === "") &&
        !(Array.isArray(cleaned) && cleaned.length === 0) &&
        !(typeof cleaned === "object" && Object.keys(cleaned).length === 0)
      ) {
        result[k] = cleaned
      }
    }
    return result as T
  }
  return obj
}
